import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../store/auth';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { useLatestRates } from '../../hooks/useLatestRates';
import { fine, money } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';

interface Summary {
  asOf: string;
  today: {
    sales: string;
    salesCount: string;
    purchases?: string;
    grossProfit?: string;
    cashReceived?: string;
    oldGoldFineWt?: string;
  };
  stock: {
    metal: string;
    purity: string;
    fineWt: string;
    costValue?: string;
  }[];
  stockTotals?: { fineWt: string; costValue: string };
  money?: {
    cash: string;
    bank: string;
    receivables: string;
    payables: string;
    customerAdvances: string;
  };
  monthToDate?: {
    revenue: string;
    grossProfit: string;
    expenses: string;
    netProfit: string;
  };
  trend: { day: string; sales: string; profit: string }[];
  topProducts: { name: string; pieces: string; value: string }[];
  attention: {
    pendingApprovals: string;
    unreadAlerts: string;
    overdueReceivables: string;
    deadStockItems: string;
    meltsOut?: string;
    meltsAwaitingApproval?: string;
    meltsOverdue?: string;
  };
}

const n = (v: string | null | undefined): number => Number(v || 0);

/** Whole rupees for the big figures; paise only where they matter. */
const rupees = (v: string | number | null | undefined): string => {
  const x = Number(v ?? 0);
  return `${x < 0 ? '−' : ''}₹${Math.abs(x).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/**
 * The shop's morning glance and evening close.
 *
 * One dark velvet panel carries the figure a jeweller opens the screen for —
 * what came in today — with the fortnight behind it. Everything else is
 * quiet white: the rates the counter is pricing from, where the money sits,
 * the month as a sum, the metal held, and only what is actually waiting for a
 * decision. Every figure opens the records behind it.
 */
export function DashboardPage() {
  const can = useAuth((s) => s.can);
  const user = useAuth((s) => s.session?.user);
  const navigate = useNavigate();
  const { header } = usePrintHeader();
  const { rates, isLoading: ratesLoading } = useLatestRates();
  const { data, error, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Summary>('/dashboard/summary'),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Loading what="Loading today" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return null;

  const financial = can('dashboard.view_financial');
  const firstName = (user?.fullName ?? '').split(' ')[0] || 'there';
  const longDate = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const waiting = [
    {
      count: n(data.attention.pendingApprovals),
      label: 'Discounts and write-offs to approve',
      to: '/approvals',
    },
    {
      count: n(data.attention.meltsAwaitingApproval),
      label: 'Melts waiting for loss approval',
      to: '/melting?tab=PENDING_APPROVAL',
    },
    {
      count: n(data.attention.meltsOverdue),
      label: 'Melts past their return-by date',
      to: '/melting?tab=IN_PROCESS',
    },
    {
      count: n(data.attention.meltsOut),
      label: 'Melts out at the karigar or refinery',
      to: '/melting?tab=IN_PROCESS',
    },
    {
      count: n(data.attention.overdueReceivables),
      label: 'Customers overdue on payment',
      to: '/reports?group=Accounting',
    },
    {
      count: n(data.attention.deadStockItems),
      label: 'Pieces unsold for over six months',
      to: '/reports?group=Stock',
    },
  ].filter((w) => w.count > 0);
  const noRateToday = !ratesLoading && (rates.length === 0 || rates.every((r) => !r.setToday));

  return (
    <div className="mx-auto max-w-[1400px]">
      {/* Who, where, when — and the three things most often started from here. */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">
            {greeting()}, {firstName}
          </h1>
          <p className="mt-0.5 text-sm text-ink-soft">
            {header.companyName}
            {header.branchName ? `, ${header.branchName}` : ''} — {longDate}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can('oldgold.post') ? (
            <Link className="btn-ghost" to="/old-gold">
              Buy old gold
            </Link>
          ) : null}
          {can('melting.create') ? (
            <Link className="btn-ghost" to="/melting?send=">
              Send for melting
            </Link>
          ) : null}
          {can('sales.post') ? (
            <Link className="btn-primary px-4" to="/pos">
              New bill
            </Link>
          ) : null}
        </div>
      </div>

      {noRateToday && can('rate.create') ? (
        <Link
          to="/rates"
          className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-gold-300 bg-gold-50 px-4 py-3 text-sm hover:border-gold-400"
        >
          <span>
            <span className="font-semibold text-gold-700">Today’s rate is not set.</span>{' '}
            <span className="text-ink-soft">
              {rates.length === 0
                ? 'Nothing can be billed until a selling rate is published.'
                : 'Bills are still pricing from an older rate.'}
            </span>
          </span>
          <span className="btn-primary shrink-0">Set today’s rate</span>
        </Link>
      ) : null}

      {/* The day, and the rates it is being priced from. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <TodayPanel
          data={data}
          financial={financial}
          canProfit={can('sales.view_profit')}
          onOpen={() => navigate('/sales')}
        />
        <RateBoard rates={rates} loading={ratesLoading} canEdit={can('rate.create')} />
      </div>

      {/* Where the money is. */}
      {financial && data.money ? (
        <section className="mt-4 grid grid-cols-2 overflow-hidden rounded-lg border border-silver-100 bg-surface shadow-card md:grid-cols-4">
          <MoneyCell label="Cash in hand" value={data.money.cash} to="/cash" />
          <MoneyCell label="In the bank" value={data.money.bank} to="/cash" />
          <MoneyCell label="Customers owe you" value={data.money.receivables} to="/accounts" tone="good" />
          <MoneyCell label="You owe suppliers" value={data.money.payables} to="/payments" tone="bad" />
        </section>
      ) : null}

      {/* The month, as the sum an accountant would write. */}
      {financial && data.monthToDate ? <MonthSum m={data.monthToDate} onOpen={() => navigate('/statements')} /> : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <MetalHeld data={data} showValue={can('stock.view_value')} />

        <Panel title="Needs your attention">
          {waiting.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
              <span className="mb-2 grid h-10 w-10 place-items-center rounded-full bg-velvet-50 text-velvet">
                <svg viewBox="0 0 20 20" className="h-5 w-5" aria-hidden="true">
                  <path
                    d="M5 10.5l3.2 3L15 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <p className="text-sm font-medium text-ink">All clear</p>
              <p className="text-2xs text-ink-soft">Nothing is waiting for a decision.</p>
            </div>
          ) : (
            <ul>
              {waiting.map((w) => (
                <li key={w.label}>
                  <button
                    onClick={() => navigate(w.to)}
                    className="flex w-full items-center gap-3 border-b border-silver-100 px-4 py-3 text-left text-sm last:border-0 hover:bg-velvet-50"
                  >
                    <span className="num grid h-8 min-w-8 place-items-center rounded-md bg-gold-100 px-2 text-sm font-bold text-gold-700">
                      {w.count}
                    </span>
                    <span className="flex-1">{w.label}</span>
                    <span className="text-ink-faint" aria-hidden="true">
                      ›
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {financial ? (
          <Panel title="Selling best this month" action={{ label: 'Sales report', to: '/reports?group=Sales' }}>
            {data.topProducts.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-ink-soft">No sales yet this month.</p>
            ) : (
              <ol>
                {data.topProducts.map((p) => {
                  const width = (n(p.value) / n(data.topProducts[0]!.value)) * 100;
                  return (
                    <li key={p.name} className="border-b border-silver-100 px-4 py-2.5 last:border-0">
                      <div className="flex items-baseline gap-2 text-sm">
                        <span className="font-medium">{p.name}</span>
                        <span className="text-2xs text-ink-soft">{p.pieces} sold</span>
                        <span className="num ml-auto font-semibold">{rupees(p.value)}</span>
                      </div>
                      <div className="mt-1.5 h-1 rounded-full bg-silver-50">
                        <div className="h-1 rounded-full bg-gold-400" style={{ width: `${width}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Panel>
        ) : null}
      </div>
    </div>
  );
}

/* ═══════════════════════════ the day ═════════════════════════════════════ */

function TodayPanel({
  data,
  financial,
  canProfit,
  onOpen,
}: {
  data: Summary;
  financial: boolean;
  canProfit: boolean;
  onOpen: () => void;
}) {
  const trend = data.trend ?? [];
  const peak = Math.max(1, ...trend.map((t) => n(t.sales)));
  const bills = n(data.today.salesCount);
  const avg = trend.length > 1 ? trend.slice(0, -1).reduce((a, t) => a + n(t.sales), 0) / (trend.length - 1) : 0;
  const vsAvg = avg > 0 ? ((n(data.today.sales) - avg) / avg) * 100 : null;

  return (
    <section className="relative overflow-hidden rounded-lg bg-rail text-rail-ink shadow-card">
      {/* A hairline of gold along the top edge, as on a jewellery case. */}
      <div className="h-1 bg-gradient-to-r from-gold-500 via-gold-300 to-gold-500" />
      <div className="grid gap-6 p-6 md:grid-cols-[minmax(0,1fr)_14rem]">
        <button onClick={onOpen} className="text-left">
          <p className="text-sm text-rail-ink/75">Sold today</p>
          <p className="num mt-1 text-5xl font-semibold tracking-tight text-rail-ink">{rupees(data.today.sales)}</p>
          <p className="mt-2 text-sm text-rail-ink/80">
            {bills === 0 ? 'No bills yet' : `${bills} ${bills === 1 ? 'bill' : 'bills'}`}
            {financial && canProfit && n(data.today.grossProfit) !== 0 ? (
              <>
                , <span className="num font-semibold text-rail-active">{rupees(data.today.grossProfit)}</span> profit
              </>
            ) : null}
            {vsAvg !== null && bills > 0 ? (
              <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-2xs">
                {vsAvg >= 0 ? '+' : ''}
                {vsAvg.toFixed(0)}% on the fortnight’s average
              </span>
            ) : null}
          </p>

          {trend.length > 0 ? (
            <div className="mt-6">
              <div className="flex h-20 items-end gap-1">
                {trend.map((t, i) => {
                  const today = i === trend.length - 1;
                  return (
                    <div key={t.day} className="group relative flex h-full flex-1 items-end">
                      <div
                        className={`w-full rounded-sm ${today ? 'bg-rail-active' : n(t.sales) > 0 ? 'bg-rail-ink/30' : 'bg-rail-ink/10'}`}
                        style={{
                          height: `${Math.max(4, (n(t.sales) / peak) * 100)}%`,
                        }}
                      />
                      <span className="pointer-events-none absolute -top-7 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-rail-line px-1.5 py-0.5 text-2xs group-hover:block">
                        {new Date(t.day).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                        })}
                        : {rupees(t.sales)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-1 flex justify-between text-2xs text-rail-ink/55">
                <span>
                  {new Date(trend[0]!.day).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
                <span>Last 14 days</span>
                <span>Today</span>
              </div>
            </div>
          ) : null}
        </button>

        <dl className="grid content-start gap-0 self-start rounded-md bg-white/5 md:mt-1">
          <DayFigure label="Cash taken" value={rupees(data.today.cashReceived)} to="/cash" />
          <DayFigure label="Bought from suppliers" value={rupees(data.today.purchases)} to="/purchases" />
          <DayFigure
            label="Old gold taken in"
            value={`${n(data.today.oldGoldFineWt).toFixed(3)} g`}
            to="/old-gold"
            last
          />
        </dl>
      </div>
    </section>
  );
}

function DayFigure({ label, value, to, last }: { label: string; value: string; to: string; last?: boolean }) {
  return (
    <Link to={to} className={`block px-4 py-3 hover:bg-white/5 ${last ? '' : 'border-b border-white/10'}`}>
      <dt className="text-2xs text-rail-ink/70">{label}</dt>
      <dd className="num text-lg font-semibold text-rail-ink">{value}</dd>
    </Link>
  );
}

/* ═══════════════════════════ rates ═══════════════════════════════════════ */

function RateBoard({
  rates,
  loading,
  canEdit,
}: {
  rates: ReturnType<typeof useLatestRates>['rates'];
  loading: boolean;
  canEdit: boolean;
}) {
  // Grouped by metal, the way a rate board in a shop window is.
  const metals = new Map<string, typeof rates>();
  for (const r of rates) {
    metals.set(r.metalName, [...(metals.get(r.metalName) ?? []), r]);
  }

  return (
    <Panel title="Today’s rates" action={canEdit ? { label: 'Update', to: '/rates' } : undefined}>
      {loading ? (
        <p className="px-4 py-6 text-sm text-ink-soft">Loading rates…</p>
      ) : rates.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-ink-soft">No selling rate has been published yet.</p>
      ) : (
        <div className="divide-y divide-silver-100">
          {[...metals].map(([metal, list]) => (
            <div key={metal} className="px-4 py-2.5">
              <p className="mb-1 text-2xs font-semibold text-ink-soft">{metal}</p>
              {list.map((r) => (
                <div key={r.key} className="flex items-baseline gap-2 py-0.5">
                  <span className="num w-14 text-sm font-medium">{r.label}</span>
                  <span className="num text-lg font-semibold text-velvet">
                    ₹
                    {n(r.value).toLocaleString('en-IN', {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  <span className="text-2xs text-ink-faint">{r.perFine ? 'per fine g' : 'per g'}</span>
                  <span className={`ml-auto text-2xs ${r.setToday ? 'text-ink-faint' : 'font-medium text-danger'}`}>
                    {r.setToday
                      ? new Date(r.setAt).toLocaleTimeString('en-IN', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : `from ${new Date(r.setAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/* ═══════════════════════════ money and month ═════════════════════════════ */

function MoneyCell({ label, value, to, tone }: { label: string; value: string; to: string; tone?: 'good' | 'bad' }) {
  return (
    <Link
      to={to}
      className="border-b border-r border-silver-100 px-5 py-4 hover:bg-velvet-50 md:border-b-0 md:last:border-r-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r"
    >
      <p className="text-sm text-ink-soft">{label}</p>
      <p
        className={`num mt-1 text-2xl font-semibold tracking-tight ${
          n(value) < 0 || (tone === 'bad' && n(value) > 0)
            ? 'text-danger'
            : tone === 'good' && n(value) > 0
              ? 'text-warn'
              : 'text-ink'
        }`}
      >
        {rupees(value)}
      </p>
    </Link>
  );
}

function MonthSum({ m, onOpen }: { m: NonNullable<Summary['monthToDate']>; onOpen: () => void }) {
  const net = n(m.netProfit);
  const margin = n(m.revenue) > 0 ? (n(m.grossProfit) / n(m.revenue)) * 100 : 0;
  const month = new Date().toLocaleDateString('en-IN', { month: 'long' });
  return (
    <button
      onClick={onOpen}
      className="mt-4 w-full rounded-lg border border-silver-100 bg-surface px-5 py-4 text-left shadow-card hover:border-velvet-200"
    >
      <div className="mb-3 flex items-baseline justify-between">
        <p className="text-sm font-semibold">{month} so far</p>
        <p className="text-2xs text-ink-soft">Open the profit and loss ›</p>
      </div>
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <SumPart label="Sales" value={rupees(m.revenue)} />
        <Op>→</Op>
        <SumPart label={`Gross profit, ${margin.toFixed(1)}% margin`} value={rupees(m.grossProfit)} />
        <Op>−</Op>
        <SumPart label="Expenses" value={rupees(m.expenses)} />
        <Op>=</Op>
        <div className={`rounded-md px-4 py-2 ${net < 0 ? 'bg-danger/10' : 'bg-success/10'}`}>
          <p className="text-2xs text-ink-soft">{net < 0 ? 'Net loss' : 'Net profit'}</p>
          <p className={`num text-2xl font-bold tracking-tight ${net < 0 ? 'text-danger' : 'text-success'}`}>
            {rupees(Math.abs(net))}
          </p>
        </div>
      </div>
    </button>
  );
}

function SumPart({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-2xs text-ink-soft">{label}</p>
      <p className="num text-xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function Op({ children }: { children: ReactNode }) {
  return <span className="pb-1 text-lg text-ink-faint">{children}</span>;
}

/* ═══════════════════════════ metal ═══════════════════════════════════════ */

function MetalHeld({ data, showValue }: { data: Summary; showValue: boolean }) {
  // Grams of different metals do not add up, so each metal is its own line.
  const metals = new Map<
    string,
    {
      fine: number;
      value: number;
      purities: { purity: string; fine: number }[];
    }
  >();
  for (const s of data.stock) {
    const m = metals.get(s.metal) ?? { fine: 0, value: 0, purities: [] };
    m.fine += n(s.fineWt);
    m.value += n(s.costValue);
    m.purities.push({ purity: s.purity, fine: n(s.fineWt) });
    metals.set(s.metal, m);
  }
  return (
    <Panel title="Metal held" action={{ label: 'Where is it', to: '/melting/position' }}>
      {metals.size === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-ink-soft">No stock yet.</p>
      ) : (
        <div className="divide-y divide-silver-100">
          {[...metals].map(([metal, m]) => (
            <div key={metal} className="px-4 py-3">
              <div className="flex items-baseline justify-between">
                <span className="font-medium">{metal}</span>
                <span className="num text-lg font-semibold">{fine(m.fine.toFixed(4))}</span>
              </div>
              <div className="mt-1 flex items-baseline justify-between text-2xs text-ink-soft">
                <span>{m.purities.map((p) => `${p.purity}: ${p.fine.toFixed(3)} g`).join('  ·  ')}</span>
                {showValue ? <span className="num">{money(m.value.toFixed(2))} at cost</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/* ═══════════════════════════ frame ═══════════════════════════════════════ */

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; to: string };
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-silver-100 bg-surface shadow-card">
      <div className="flex items-baseline justify-between border-b border-silver-100 px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {action ? (
          <Link to={action.to} className="text-2xs font-medium text-velvet hover:underline">
            {action.label}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}
